import { useState } from 'react'

import { PageHead } from '../../components/primitives/PageHead'
import { useOrgConfig } from '../../lib/org-config-context'
import { AdminMoreConfigTab } from './AdminMoreConfigTab'
import { AdminRegionTab } from './AdminRegionTab'
import { AdminUsersTab } from './AdminUsersTab'

type TabKey = 'region' | 'users' | 'more'

interface TabDef {
  key: TabKey
  label: string
}

const TABS: ReadonlyArray<TabDef> = [
  { key: 'region', label: 'Region & holidays' },
  { key: 'users', label: 'Users & roles' },
  { key: 'more', label: 'More config' },
]

/** White-label admin hub — product config. admin+ only (gated by the route).
 *  Feature flags are controlled via environment variables, not this UI.
 *  Branding & Theme moved to its own superadmin-only page at /admin/branding. */
export function AdminHubPage(): JSX.Element {
  const org = useOrgConfig()

  const tabs = TABS
  const [tab, setTab] = useState<TabKey>('region')

  return (
    <>
      <PageHead
        eyebrow="Admin"
        title="Admin console"
        subtitle={`Manage ${org.appName}: region, user roles, and configuration.`}
      />
      <div
        role="tablist"
        aria-label="Admin sections"
        style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 20 }}
      >
        {tabs.map((t) => {
          const active = t.key === tab
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              style={{
                appearance: 'none',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: '10px 14px',
                minHeight: 44,
                fontFamily: 'var(--font-sans)',
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                color: active ? 'var(--primary)' : 'var(--text-2)',
                borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
                transition: 'color 120ms ease, border-color 120ms ease',
              }}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'region' ? <AdminRegionTab /> : null}
      {tab === 'users' ? <AdminUsersTab /> : null}
      {tab === 'more' ? <AdminMoreConfigTab /> : null}
    </>
  )
}
